import type { TableBillingSegmentSource } from "./tableBillingRecordSource"
import type { TableConsumeInfoExt, TableTransferBatchInfo } from "@/api/business/types/table"
import dayjs from "dayjs"
import { computedMinutes } from "@/utils/datetime"
import { isNumberEqual } from "@/utils/math"

export type TableBillingDetailRow = {
  key: string
  label: string
  sourceText?: string
  transferOperatorName?: string
  ruleText?: string
  durationText: string
  amount: number
  originalAmount: number
  amountCalculated?: boolean
}

export type TableBillingDetailSection = {
  key: string
  sourceText: string
  transferOperatorName: string
  ruleText: string
  timeText: string
  durationText: string
  amount: number
  originalAmount: number
  amountCalculated?: boolean
  rows: TableBillingDetailRow[]
  isTransfer: boolean
  isCurrent: boolean
  defaultExpanded: boolean
  canUpdateBillingType: boolean
  rollbackBatch?: TableTransferBatchInfo
}

type TableBillingFeePart = Pick<TableBillingDetailRow, "durationText" | "label" | "originalAmount">

type BuildTableBillingDetailRowsOptions = {
  fallbackBillingRule?: unknown
  isPreOpen: boolean
  tableFeeList: TableConsumeInfoExt[]
  tableRuleList: unknown[]
}

type BuildTableBillingDetailSectionsOptions = BuildTableBillingDetailRowsOptions & {
  hideZeroAmountTransferSections?: boolean
  tableTransferBatchList?: TableTransferBatchInfo[]
}

const asRecord = (source: unknown): Record<string, unknown> | undefined => {
  if (!source) {
    return undefined
  }
  if (typeof source === "object") {
    return source as Record<string, unknown>
  }
  if (typeof source === "string") {
    try {
      const parsed = JSON.parse(source)
      return parsed && typeof parsed === "object" ? parsed as Record<string, unknown> : undefined
    } catch (_error) {
      return undefined
    }
  }
  return undefined
}

const firstNumberField = (source: unknown, keys: string[]) => {
  const record = asRecord(source)
  if (!record) {
    return undefined
  }
  for (const key of keys) {
    const value = record[key]
    if (value === undefined || value === null || value === "") {
      continue
    }
    const numberValue = Number(value)
    if (Number.isFinite(numberValue)) {
      return numberValue
    }
  }
  return undefined
}

const firstStringField = (source: unknown, keys: string[]) => {
  const record = asRecord(source)
  if (!record) {
    return ""
  }
  for (const key of keys) {
    const value = record[key]
    if (typeof value === "string" && value.trim()) {
      return value.trim()
    }
  }
  return ""
}

const firstOperatorName = (source: unknown) => {
  const directName = firstStringField(source, [
    "transferOperatorName",
    "operatorName",
    "operationUserName",
    "operateUserName",
    "merchantUserName",
    "orderMerchantUserName",
    "createUserName",
    "creatorName",
    "createdByName",
    "createByName",
  ])
  if (directName) {
    return directName
  }

  const createBy = firstStringField(source, ["createBy", "createdBy"])
  if (!createBy) {
    return ""
  }
  try {
    const userSnapshot = JSON.parse(createBy)
    return firstStringField(userSnapshot, ["username", "name", "realName", "userName"])
  } catch (_error) {
    return /^\d+$/.test(createBy) ? "" : createBy
  }
}

const getRuleDetailRecord = (source: unknown) => {
  const record = asRecord(source)
  const tableBillingFields = asRecord(record?.tableBillingFields)
  return asRecord(record?.ruleDetailVo) ?? asRecord(record?.ruleDetail) ?? asRecord(tableBillingFields?.ruleDetail)
}

const getServiceFeeDetailRecord = (source: unknown) => {
  const record = asRecord(source)
  const tableBillingFields = asRecord(record?.tableBillingFields)
  return asRecord(record?.serviceFeeDetailInfo)
    ?? asRecord(record?.serviceFeeDetail)
    ?? asRecord(record?.service_fee_detail)
    ?? asRecord(tableBillingFields?.serviceFeeDetail)
    ?? asRecord(tableBillingFields?.service_fee_detail)
}

const getTableBillingRule = (item: TableConsumeInfoExt, tableRuleList: unknown[], fallbackBillingRule?: unknown) => {
  const ruleId = firstNumberField(item, ["ruleId", "tableBillingRuleId"])
  if (ruleId === undefined) {
    return fallbackBillingRule
  }
  const matchedRule = tableRuleList.find((ruleItem) => {
    return firstNumberField(ruleItem, ["tableBillingRuleId", "ruleId", "id"]) === ruleId
  })
  if (matchedRule) {
    return matchedRule
  }
  const fallbackRuleId = firstNumberField(fallbackBillingRule, ["tableBillingRuleId", "ruleId", "id"])
  return fallbackRuleId === undefined || fallbackRuleId === ruleId ? fallbackBillingRule : undefined
}

const getTableDisplayName = (item: TableConsumeInfoExt) => {
  return firstStringField(item, ["tableName"]) || `桌台${firstNumberField(item, ["tableId"]) ?? "--"}`
}

const isTransferSegment = (item: TableConsumeInfoExt) => {
  const record = item as unknown as Record<string, unknown>
  return record.transferFlag === true || record.transferFlag === 1 || record.transferFlag === "1"
}

export const canUpdateTransferBillingType = (batch?: TableTransferBatchInfo) => {
  return typeof batch?.transferBatchId === "string"
    && (batch.transferBatchId.startsWith("MOVE-") || batch.transferBatchId.startsWith("TABLE-"))
}

const shouldApplyPreOpenZero = (item: TableConsumeInfoExt, isPreOpen: boolean) => {
  return isPreOpen && !isTransferSegment(item)
}

const isMigrationSegment = (item: TableConsumeInfoExt) => {
  const record = item as unknown as Record<string, unknown>
  return record.isMigration === true || record.isMigration === 1 || record.isMigration === "1"
}

const getTableSegmentSourceText = (item: TableConsumeInfoExt, index: number, total: number) => {
  if (isTransferSegment(item)) {
    return `转自 ${firstStringField(item, ["sourceTableName"]) || getTableDisplayName(item)}`
  }
  if (total <= 1) {
    return ""
  }
  return `${index === total - 1 ? "当前" : "转自"} ${getTableDisplayName(item)}`
}

const getTransferSegmentSourceText = (item: TableConsumeInfoExt) => {
  if (!isTransferSegment(item)) {
    return ""
  }
  const sourceTableName = firstStringField(item, ["sourceTableName"]) || getTableDisplayName(item)
  return `转自 ${formatTableSourceText(sourceTableName)}`
}

const formatTableSourceText = (tableName: string) => {
  return tableName.endsWith("桌台") ? tableName : `${tableName}桌台`
}

const getHistoricalSegmentSourceText = (item: TableConsumeInfoExt) => {
  return `转自 ${formatTableSourceText(getTableDisplayName(item))}`
}

const getRuleDisplayName = (item: TableConsumeInfoExt, ruleRecord: unknown) => {
  return firstStringField(item, ["ruleName", "tableBillingRuleName"])
    || firstStringField(ruleRecord, ["ruleName", "name"])
}

const getRuleType = (item: TableConsumeInfoExt, ruleRecord: unknown, ruleDetail: unknown) => {
  const ruleType = firstNumberField(item, ["ruleType", "billingRuleType", "tableBillingType"])
    ?? firstNumberField(ruleRecord, ["ruleType", "billingRuleType", "tableBillingType"])
    ?? firstNumberField(ruleDetail, ["ruleType"])
  if (ruleType === 1 || ruleType === 2) {
    return ruleType
  }

  const ruleName = firstStringField(item, ["ruleName", "tableBillingRuleName"])
    || firstStringField(ruleRecord, ["ruleName", "name"])
  if (ruleName.includes("包")) {
    return 2
  }
  if (ruleName.includes("计时") || ruleName.includes("时间")) {
    return 1
  }
  return undefined
}

const parseDurationTextToMinutes = (durationText?: null | number | string) => {
  if (durationText === undefined || durationText === null || durationText === "") {
    return undefined
  }
  if (typeof durationText === "number" && Number.isFinite(durationText)) {
    return durationText
  }
  const durationTextValue = String(durationText)
  const hourMatch = durationTextValue.match(/(\d+(?:\.\d+)?)\s*小时/)
  const minuteMatch = durationTextValue.match(/(\d+(?:\.\d+)?)\s*分钟/)
  if (!hourMatch && !minuteMatch) {
    return undefined
  }
  return Math.floor(Number(hourMatch?.[1] ?? 0) * 60 + Number(minuteMatch?.[1] ?? 0))
}

const formatBillingDuration = (minutes: number | undefined, fallback?: unknown) => {
  if (minutes !== undefined && Number.isFinite(minutes)) {
    return computedMinutes(Math.max(Math.floor(minutes), 0))
  }
  if (fallback === undefined || fallback === null || fallback === "") {
    return "--"
  }
  return String(fallback).trim() || "--"
}

const getDurationFromTimeRange = (item: TableConsumeInfoExt) => {
  if (!item.checkIn) {
    return undefined
  }
  const checkIn = dayjs(item.checkIn)
  const checkOut = item.checkOut ? dayjs(item.checkOut) : dayjs()
  if (!checkIn.isValid() || !checkOut.isValid()) {
    return undefined
  }
  return Math.max(checkOut.diff(checkIn, "minute"), 0)
}

const getRecordStayMinutes = (item: TableConsumeInfoExt) => {
  return firstNumberField(item, ["rawStayDuration", "rawStayMinutes", "stayMinutes", "durationMinutes", "consumeDuration", "consumeMinutes", "usageDuration"])
    ?? parseDurationTextToMinutes(item.stayDuration)
    ?? getDurationFromTimeRange(item)
}

const getPackageDurationMinutes = (item: TableConsumeInfoExt, ruleDetail: unknown) => {
  return firstNumberField(item, [
    "packageDuration",
    "packageMinutes",
    "baseDuration",
    "baseDurationMinutes",
    "package_duration",
    "base_duration",
  ])
    ?? firstNumberField(ruleDetail, [
      "packageDuration",
      "baseDuration",
      "package_duration",
      "base_duration",
    ])
}

const getOvertimeDurationMinutes = (item: TableConsumeInfoExt, packageDuration: number | undefined) => {
  const explicitMinutes = firstNumberField(item, ["overtimeMinutes", "overTimeMinutes", "timeoutMinutes", "overtimeDurationMinutes", "timeoutDurationMinutes"])
  if (explicitMinutes !== undefined) {
    return Math.max(explicitMinutes, 0)
  }
  const stayMinutes = getRecordStayMinutes(item)
  if (stayMinutes === undefined || packageDuration === undefined) {
    return undefined
  }
  return Math.max(stayMinutes - packageDuration, 0)
}

const getPackageChargeDurationMinutes = (item: TableConsumeInfoExt, packageDuration: number | undefined) => {
  const explicitMinutes = firstNumberField(item, [
    "packageChargeMinutes",
    "packageChargeDuration",
    "packageChargedMinutes",
    "packageDurationMinutes",
    "baseChargeMinutes",
    "baseChargeDuration",
    "package_charge_minutes",
    "package_duration_minutes",
  ])
  if (explicitMinutes !== undefined) {
    return Math.max(explicitMinutes, 0)
  }
  const stayMinutes = getRecordStayMinutes(item)
  if (stayMinutes === undefined) {
    return undefined
  }
  if (packageDuration === undefined) {
    return undefined
  }
  return Math.min(Math.max(stayMinutes, 0), Math.max(packageDuration, 0))
}

const getServiceDurationFromRule = (item: TableConsumeInfoExt, serviceFeeDetail?: unknown) => {
  const serviceStartHour = firstNumberField(serviceFeeDetail, ["startTime", "serviceStartTime", "service_start_time"])
  const serviceEndHour = firstNumberField(serviceFeeDetail, ["endTime", "serviceEndTime", "service_end_time"])
  const checkIn = item.checkIn ? dayjs(item.checkIn) : undefined
  const checkOut = item.checkOut ? dayjs(item.checkOut) : dayjs()
  if (
    serviceStartHour === undefined
    || serviceEndHour === undefined
    || !checkIn?.isValid()
    || !checkOut.isValid()
    || !checkOut.isAfter(checkIn)
  ) {
    return undefined
  }

  const startHour = Math.max(Math.min(Math.floor(serviceStartHour), 24), 0)
  const endHour = Math.max(Math.min(Math.floor(serviceEndHour), 24), 0)
  if (startHour === endHour) {
    return 0
  }

  let totalMinutes = 0
  let cursor = checkIn.startOf("day").subtract(1, "day")
  const lastDay = checkOut.startOf("day")
  while (cursor.isBefore(lastDay) || cursor.isSame(lastDay)) {
    const windowStart = cursor.add(startHour, "hour")
    const windowEnd = startHour > endHour
      ? cursor.add(1, "day").add(endHour, "hour")
      : cursor.add(endHour, "hour")
    const overlapStart = windowStart.isAfter(checkIn) ? windowStart : checkIn
    const overlapEnd = windowEnd.isBefore(checkOut) ? windowEnd : checkOut
    if (overlapEnd.isAfter(overlapStart)) {
      totalMinutes += overlapEnd.diff(overlapStart, "minute")
    }
    cursor = cursor.add(1, "day")
  }

  const stayMinutes = getRecordStayMinutes(item)
  return stayMinutes === undefined ? totalMinutes : Math.min(totalMinutes, stayMinutes)
}

const getServiceDurationMinutes = (item: TableConsumeInfoExt, serviceFeeDetail?: unknown) => {
  const explicitMinutes = firstNumberField(item, [
    "serviceFeeMinutes",
    "serviceMinutes",
    "serviceDurationMinutes",
    "serviceFeeDuration",
    "serviceDuration",
    "service_fee_minutes",
    "service_minutes",
    "service_fee_duration",
    "service_duration",
  ])
  if (explicitMinutes !== undefined) {
    return explicitMinutes
  }
  return getServiceDurationFromRule(item, serviceFeeDetail) ?? getRecordStayMinutes(item)
}

const toNonNegativeAmount = (amount: number | undefined) => Math.max(Number(amount ?? 0), 0)

const applyTableDiscountToParts = (parts: TableBillingFeePart[], discountedTotal: number) => {
  const originalAmounts = parts.map(part => Math.max(Math.round(part.originalAmount), 0))
  const originalTotal = originalAmounts.reduce((total, amount) => total + amount, 0)
  if (originalTotal <= 0) {
    return parts.map(part => ({ ...part, amount: 0 }))
  }

  const boundedDiscountedTotal = Math.min(Math.max(Math.round(discountedTotal), 0), originalTotal)
  if (isNumberEqual(boundedDiscountedTotal, originalTotal)) {
    return parts.map((part, index) => ({ ...part, amount: originalAmounts[index] }))
  }

  const quotas = originalAmounts.map(amount => amount * boundedDiscountedTotal / originalTotal)
  const allocatedAmounts = quotas.map(Math.floor)
  let remainingAmount = boundedDiscountedTotal - allocatedAmounts.reduce((total, amount) => total + amount, 0)
  const remainderOrder = quotas
    .map((quota, index) => ({ fraction: quota - allocatedAmounts[index], index }))
    .sort((left, right) => right.fraction - left.fraction || left.index - right.index)
  for (const { index } of remainderOrder) {
    if (remainingAmount <= 0) {
      break
    }
    if (allocatedAmounts[index] < originalAmounts[index]) {
      allocatedAmounts[index] += 1
      remainingAmount -= 1
    }
  }

  return parts.map((part, index) => ({
    ...part,
    amount: allocatedAmounts[index],
  }))
}

const getPackageTableFeeParts = (item: TableConsumeInfoExt, ruleDetail: unknown, originalTableFee: number, isPreOpen: boolean) => {
  const packageDuration = getPackageDurationMinutes(item, ruleDetail)
  const packageChargeDuration = getPackageChargeDurationMinutes(item, packageDuration)
  const overtimeDuration = getOvertimeDurationMinutes(item, packageDuration)
  const explicitPackageAmount = firstNumberField(item, ["packageFee", "packageAmount", "baseFee", "baseAmount", "baseIncome", "packageIncome"])
  const explicitOvertimeAmount = firstNumberField(item, ["overtimeFee", "overTimeFee", "timeoutFee", "overtimeAmount", "overTimeAmount", "timeoutAmount", "overtimeIncome", "timeoutIncome"])
  const rulePackagePrice = firstNumberField(ruleDetail, ["packagePrice", "basePrice"])

  let packageAmount = originalTableFee
  let overtimeAmount = 0

  if (explicitOvertimeAmount !== undefined) {
    overtimeAmount = Math.min(toNonNegativeAmount(explicitOvertimeAmount), originalTableFee)
    packageAmount = Math.max(originalTableFee - overtimeAmount, 0)
  } else if (explicitPackageAmount !== undefined) {
    packageAmount = Math.min(toNonNegativeAmount(explicitPackageAmount), originalTableFee)
    overtimeAmount = Math.max(originalTableFee - packageAmount, 0)
  } else if (rulePackagePrice !== undefined && originalTableFee > rulePackagePrice) {
    packageAmount = toNonNegativeAmount(rulePackagePrice)
    overtimeAmount = Math.max(originalTableFee - packageAmount, 0)
  }

  const parts: TableBillingFeePart[] = [
    {
      label: "包段收费",
      durationText: formatBillingDuration(isPreOpen ? 0 : packageChargeDuration),
      originalAmount: packageAmount,
    },
  ]

  if (overtimeAmount > 0 || (overtimeDuration ?? 0) > 0) {
    parts.push({
      label: "超时收费",
      durationText: formatBillingDuration(isPreOpen ? 0 : overtimeDuration),
      originalAmount: overtimeAmount,
    })
  }

  return parts
}

const getTableFeeParts = (item: TableConsumeInfoExt, tableRuleList: unknown[], isPreOpen: boolean, fallbackBillingRule?: unknown) => {
  const ruleRecord = getTableBillingRule(item, tableRuleList, fallbackBillingRule)
  const ruleDetail = getRuleDetailRecord(ruleRecord) ?? getRuleDetailRecord(item)
  const ruleType = getRuleType(item, ruleRecord, ruleDetail)
  const originalTableFee = isPreOpen ? 0 : toNonNegativeAmount(item.tableFee ?? undefined)
  const discountedTableFee = isPreOpen ? 0 : toNonNegativeAmount(item.afterDiscountTableFee ?? originalTableFee)

  const parts = ruleType === 2
    ? getPackageTableFeeParts(item, ruleDetail, originalTableFee, isPreOpen)
    : [
        {
          label: "计时收费",
          durationText: formatBillingDuration(isPreOpen ? 0 : getRecordStayMinutes(item), isPreOpen ? undefined : item.stayDuration),
          originalAmount: originalTableFee,
        },
      ]

  return applyTableDiscountToParts(parts, discountedTableFee)
}

const getTableServiceFeeRow = (item: TableConsumeInfoExt, index: number, isPreOpen: boolean, serviceFeeDetail?: unknown): TableBillingDetailRow | undefined => {
  const originalServiceFee = isPreOpen ? 0 : toNonNegativeAmount(firstNumberField(item, ["serviceTotalFee", "serviceFee", "serviceAmount"]))
  const afterDiscountServiceFee = isPreOpen ? 0 : toNonNegativeAmount(item.afterDiscountServiceFee ?? originalServiceFee)
  if (originalServiceFee <= 0 && afterDiscountServiceFee <= 0) {
    return undefined
  }
  return {
    key: `service-${index}`,
    label: "服务费",
    durationText: formatBillingDuration(isPreOpen ? 0 : getServiceDurationMinutes(item, serviceFeeDetail), isPreOpen ? undefined : item.stayDuration),
    amount: afterDiscountServiceFee,
    originalAmount: originalServiceFee,
  }
}

const formatTableBillingSectionTime = (time?: null | string) => {
  if (!time) {
    return ""
  }
  const parsedTime = dayjs(time)
  return parsedTime.isValid() ? parsedTime.format("YYYY-MM-DD HH:mm:ss") : time
}

const formatTableBillingSegmentTimeRange = (segment: TableBillingSegmentSource) => {
  const startText = formatTableBillingSectionTime(segment.startTime)
  const endText = formatTableBillingSectionTime(segment.endTime ?? segment.chargeUntilTime)
  if (startText && endText) {
    return `${startText} - ${endText}`
  }
  return startText
}

const isSameValue = (left: unknown, right: unknown) => {
  if (left === undefined || left === null || left === "") return false
  if (right === undefined || right === null || right === "") return false
  return String(left) === String(right)
}

const getTransferBatchForSection = (
  item: TableConsumeInfoExt,
  tableTransferBatchList: TableTransferBatchInfo[],
  isCurrentSegment: boolean,
) => {
  if (!isTransferSegment(item)) {
    if (!isCurrentSegment) {
      return undefined
    }
    return getEmptyTargetMoveBatchForSection(item, tableTransferBatchList)
  }
  const record = item as unknown as Record<string, unknown>
  return tableTransferBatchList.find((batch) => {
    if (isSameValue(record.transferBatchId, batch.transferBatchId)) return true
    if (isSameValue(item.sourceTableRecordId, batch.sourceTableRecordId)) return true
    if (isSameValue(item.sourceTableId, batch.sourceTableId) && firstStringField(item, ["sourceTableName"]) === (batch.sourceTableName ?? "")) return true
    return false
  })
}

const isEmptyTargetMoveBatch = (batch: TableTransferBatchInfo) => {
  return typeof batch.transferBatchId === "string" && batch.transferBatchId.startsWith("MOVE-")
}

const isExistingTargetMoveBatch = (batch: TableTransferBatchInfo) => {
  return typeof batch.transferBatchId === "string" && batch.transferBatchId.startsWith("TABLE-")
}

export const hasExistingTargetTransferDisplay = (
  tableFeeList?: TableConsumeInfoExt[] | null,
  tableTransferBatchList?: TableTransferBatchInfo[] | null,
) => {
  return Array.isArray(tableFeeList)
    && tableFeeList.some(isTransferSegment)
    && Array.isArray(tableTransferBatchList)
    && tableTransferBatchList.some(isExistingTargetMoveBatch)
}

const getEmptyTargetMoveBatchForSection = (
  item: TableConsumeInfoExt,
  tableTransferBatchList: TableTransferBatchInfo[],
) => {
  return tableTransferBatchList.find((batch) => {
    if (!isEmptyTargetMoveBatch(batch)) {
      return false
    }
    return isSameValue(item.tableId, batch.targetTableId)
  })
}

const isServerSegmentFromBatchSource = (
  segment: TableBillingSegmentSource,
  batch: TableTransferBatchInfo,
) => {
  return isSameValue(segment.tableId, batch.sourceTableId)
    || Boolean(segment.tableName && segment.tableName === batch.sourceTableName)
}

const isServerSegmentOnBatchTarget = (
  segment: TableBillingSegmentSource,
  batch: TableTransferBatchInfo,
) => {
  return isSameValue(segment.tableId, batch.targetTableId)
    || Boolean(segment.tableName && segment.tableName === batch.targetTableName)
}

const compareServerSegmentLatestFirst = (
  leftIndex: number,
  rightIndex: number,
  segments: TableBillingSegmentSource[],
) => {
  const left = segments[leftIndex]
  const right = segments[rightIndex]
  const leftTime = dayjs(left.startTime).valueOf()
  const rightTime = dayjs(right.startTime).valueOf()
  if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
    return rightTime - leftTime
  }
  const segmentNoDiff = (right.segmentNo ?? -1) - (left.segmentNo ?? -1)
  return segmentNoDiff !== 0 ? segmentNoDiff : rightIndex - leftIndex
}

const isServerSegmentAtBatchTransferTime = (
  segment: TableBillingSegmentSource,
  batch: TableTransferBatchInfo,
) => {
  const segmentEndTime = segment.endTime ?? segment.chargeUntilTime
  if (!segmentEndTime || !batch.transferTime) {
    return false
  }
  const parsedSegmentEndTime = dayjs(segmentEndTime)
  const parsedTransferTime = dayjs(batch.transferTime)
  return parsedSegmentEndTime.isValid()
    && parsedTransferTime.isValid()
    && parsedSegmentEndTime.valueOf() === parsedTransferTime.valueOf()
}

const buildServerSegmentBatchAssignments = (
  segments: TableBillingSegmentSource[],
  tableTransferBatchList: TableTransferBatchInfo[],
  currentSegmentIndex: number,
) => {
  const assignments = new Map<number, TableTransferBatchInfo>()
  const assignedBatchIds = new Set<string>()

  const assignBatch = (batch: TableTransferBatchInfo, candidateIndexes: number[]) => {
    const batchId = batch.transferBatchId
    if (!batchId || assignedBatchIds.has(batchId)) {
      return false
    }
    const segmentIndex = candidateIndexes.find(index => !assignments.has(index))
    if (segmentIndex === undefined) {
      return false
    }
    assignments.set(segmentIndex, batch)
    assignedBatchIds.add(batchId)
    return true
  }

  const orderedBatches = tableTransferBatchList
    .map((batch, index) => ({ batch, index }))
    .sort((left, right) => Number(Boolean(right.batch.canRollback)) - Number(Boolean(left.batch.canRollback))
      || left.index - right.index)
    .map(item => item.batch)

  orderedBatches.filter(isExistingTargetMoveBatch).forEach((batch) => {
    const allSourceSegmentIndexes = segments
      .map((segment, index) => ({ index, segment }))
      .filter(({ index, segment }) => index !== currentSegmentIndex
        && segment.segmentSource === "TRANSFER"
        && isServerSegmentFromBatchSource(segment, batch))
      .map(item => item.index)
    const transferBoundaryIndexes = allSourceSegmentIndexes.filter(index => {
      return isServerSegmentAtBatchTransferTime(segments[index], batch)
    })
    const sourceSegmentIndexes = (transferBoundaryIndexes.length > 0
      ? transferBoundaryIndexes
      : allSourceSegmentIndexes)
      .sort((left, right) => compareServerSegmentLatestFirst(left, right, segments))
    assignBatch(batch, sourceSegmentIndexes)
  })

  orderedBatches.filter(isEmptyTargetMoveBatch).forEach((batch) => {
    const targetSegmentIndexes = segments
      .map((segment, index) => ({ index, segment }))
      .filter(({ segment }) => isServerSegmentOnBatchTarget(segment, batch))
      .map(item => item.index)
      .sort((left, right) => {
        if (left === currentSegmentIndex) return -1
        if (right === currentSegmentIndex) return 1
        return compareServerSegmentLatestFirst(left, right, segments)
      })
    assignBatch(batch, targetSegmentIndexes)
  })

  const currentSegment = segments[currentSegmentIndex]
  if (currentSegment) {
    orderedBatches.filter(isExistingTargetMoveBatch).forEach((batch) => {
      if (isServerSegmentOnBatchTarget(currentSegment, batch)) {
        assignBatch(batch, [currentSegmentIndex])
      }
    })
  }

  return assignments
}

const getServerSegmentSourceText = (
  segment: TableBillingSegmentSource,
  rollbackBatch?: TableTransferBatchInfo,
) => {
  if (!rollbackBatch) {
    return segment.segmentSourceName || ""
  }
  const sourceTableName = rollbackBatch.sourceTableName || `桌台${rollbackBatch.sourceTableId ?? "--"}`
  return `转自 ${formatTableSourceText(sourceTableName)}`
}

const isServerSegmentAmountCalculated = (segment: TableBillingSegmentSource) => {
  if (segment.feeCalculated === false) {
    return false
  }
  return segment.totalFee !== undefined && segment.totalFee !== null
}

const isExplicitZeroAmountTransferSegment = (segment: TableBillingSegmentSource) => {
  if (segment.segmentSource !== "TRANSFER"
    || segment.latest
    || segment.feeCalculated !== true) {
    return false
  }
  if (segment.totalFee === undefined
    || segment.totalFee === null
    || !isNumberEqual(segment.totalFee, 0)) {
    return false
  }
  return segment.stayMinutes === 0 || segment.stayDuration?.trim() === "0小时0分钟"
}

const shouldHideZeroAmountTransferSegment = (
  segment: TableBillingSegmentSource,
  rollbackBatch: TableTransferBatchInfo | undefined,
  hideZeroAmountTransferSections: boolean,
) => {
  return hideZeroAmountTransferSections
    && Boolean(rollbackBatch && isExistingTargetMoveBatch(rollbackBatch))
    && isExplicitZeroAmountTransferSegment(segment)
}

const getServerSegmentTimeText = (
  segment: TableBillingSegmentSource,
  rollbackBatch: TableTransferBatchInfo | undefined,
) => {
  if (rollbackBatch
    && isExistingTargetMoveBatch(rollbackBatch)
    && rollbackBatch.transferTime
    && isExplicitZeroAmountTransferSegment(segment)) {
    return formatTableBillingSectionTime(rollbackBatch.transferTime)
  }
  return formatTableBillingSegmentTimeRange(segment)
}

const getCurrentMoveTargetSegmentIndex = (
  visibleTableFeeList: TableConsumeInfoExt[],
  tableTransferBatchList: TableTransferBatchInfo[],
) => {
  const currentMoveBatch = tableTransferBatchList.find(batch => isEmptyTargetMoveBatch(batch) && batch.canRollback)
    ?? tableTransferBatchList.find(isEmptyTargetMoveBatch)
  if (!currentMoveBatch) {
    return undefined
  }
  for (let index = visibleTableFeeList.length - 1; index >= 0; index -= 1) {
    const item = visibleTableFeeList[index]
    if (!isTransferSegment(item) && isSameValue(item.tableId, currentMoveBatch.targetTableId)) {
      return index
    }
  }
  return undefined
}

const getCurrentTableBillingSegmentIndex = (
  visibleTableFeeList: TableConsumeInfoExt[],
  tableTransferBatchList: TableTransferBatchInfo[],
) => {
  const currentMoveTargetIndex = getCurrentMoveTargetSegmentIndex(visibleTableFeeList, tableTransferBatchList)
  if (currentMoveTargetIndex !== undefined) {
    return currentMoveTargetIndex
  }
  for (let index = visibleTableFeeList.length - 1; index >= 0; index -= 1) {
    if (!isTransferSegment(visibleTableFeeList[index])) {
      return index
    }
  }
  return visibleTableFeeList.length - 1
}

const getTableBillingSectionSourceText = (
  item: TableConsumeInfoExt,
  index: number,
  currentSegmentIndex: number,
  visibleTableFeeListLength: number,
  rollbackBatch?: TableTransferBatchInfo,
) => {
  if (isTransferSegment(item)) {
    return getTransferSegmentSourceText(item)
  }
  if (rollbackBatch) {
    const sourceTableName = firstStringField(rollbackBatch, ["sourceTableName", "source_table_name", "transferSourceTableName"])
    const sourceTableId = firstNumberField(rollbackBatch, ["sourceTableId", "source_table_id", "transferSourceTableId"])
    return `转自 ${formatTableSourceText(sourceTableName || `桌台${sourceTableId ?? "--"}`)}`
  }
  if (visibleTableFeeListLength > 1 && index !== currentSegmentIndex) {
    return getHistoricalSegmentSourceText(item)
  }
  return ""
}

const getTableTransferOperatorName = (
  item: TableConsumeInfoExt,
  rollbackBatch?: TableTransferBatchInfo,
) => {
  return firstOperatorName(item) || firstOperatorName(rollbackBatch)
}

const getTableBillingSectionTimeValue = (item: TableConsumeInfoExt) => {
  const parsedTime = dayjs(item.transferTime || item.checkIn)
  return parsedTime.isValid() ? parsedTime.valueOf() : Number.NEGATIVE_INFINITY
}

const shouldHideZeroAmountTransferSection = (
  section: Pick<TableBillingDetailSection, "amount" | "isTransfer" | "originalAmount"> & { isCurrentSegment: boolean },
  hideZeroAmountTransferSections?: boolean,
) => {
  return Boolean(hideZeroAmountTransferSections)
    && section.isTransfer
    && !section.isCurrentSegment
    && isNumberEqual(section.amount || 0, 0)
    && isNumberEqual(section.originalAmount || 0, 0)
}

export const buildTableBillingDetailRows = ({
  fallbackBillingRule,
  isPreOpen,
  tableFeeList,
  tableRuleList,
}: BuildTableBillingDetailRowsOptions): TableBillingDetailRow[] => {
  const visibleTableFeeList = tableFeeList.filter(item => !isMigrationSegment(item))

  return visibleTableFeeList.flatMap((item, index) => {
    const segmentIsPreOpen = shouldApplyPreOpenZero(item, isPreOpen)
    const ruleRecord = getTableBillingRule(item, tableRuleList, fallbackBillingRule)
    const sourceText = getTableSegmentSourceText(item, index, visibleTableFeeList.length)
    const transferOperatorName = getTableTransferOperatorName(item)
    const ruleText = getRuleDisplayName(item, ruleRecord)
    const metadata = {
      ...(sourceText ? { sourceText } : {}),
      ...(transferOperatorName ? { transferOperatorName } : {}),
      ...(ruleText ? { ruleText } : {}),
    }
    const tableRows = getTableFeeParts(item, tableRuleList, segmentIsPreOpen, fallbackBillingRule).map((row, rowIndex) => ({
      key: `table-${index}-${rowIndex}`,
      label: row.label,
      ...metadata,
      durationText: row.durationText,
      amount: row.amount,
      originalAmount: row.originalAmount,
    }))
    const serviceFeeDetail = getServiceFeeDetailRecord(ruleRecord) ?? getServiceFeeDetailRecord(item)
    const serviceRow = getTableServiceFeeRow(item, index, segmentIsPreOpen, serviceFeeDetail)
    return serviceRow ? [...tableRows, { ...serviceRow, ...metadata }] : tableRows
  })
}

export const buildTableBillingDetailSections = ({
  fallbackBillingRule,
  hideZeroAmountTransferSections,
  isPreOpen,
  tableFeeList,
  tableRuleList,
  tableTransferBatchList = [],
}: BuildTableBillingDetailSectionsOptions): TableBillingDetailSection[] => {
  const visibleTableFeeList = tableFeeList.filter(item => !isMigrationSegment(item))
  const currentSegmentIndex = getCurrentTableBillingSegmentIndex(visibleTableFeeList, tableTransferBatchList)

  const sections = visibleTableFeeList.map((item, index) => {
    const segmentIsPreOpen = shouldApplyPreOpenZero(item, isPreOpen)
    const ruleRecord = getTableBillingRule(item, tableRuleList, fallbackBillingRule)
    const isCurrentSegment = index === currentSegmentIndex
    const isTransferSection = isTransferSegment(item) || (!isCurrentSegment && visibleTableFeeList.length > 1)
    const ruleText = getRuleDisplayName(item, ruleRecord)
    const rows = buildTableBillingDetailRows({
      fallbackBillingRule,
      isPreOpen: segmentIsPreOpen,
      tableFeeList: [item],
      tableRuleList,
    })
    const amount = rows.reduce((total, row) => total + row.amount, 0)
    const originalAmount = rows.reduce((total, row) => total + row.originalAmount, 0)
    const rollbackBatch = getTransferBatchForSection(item, tableTransferBatchList, isCurrentSegment)
    const sourceText = getTableBillingSectionSourceText(item, index, currentSegmentIndex, visibleTableFeeList.length, rollbackBatch)
    const transferOperatorName = getTableTransferOperatorName(item, rollbackBatch)

    return {
      originalIndex: index,
      isCurrentSegment,
      timeValue: getTableBillingSectionTimeValue(item),
      key: `${item.tableId ?? "table"}-${item.sourceTableRecordId ?? item.checkIn ?? index}-${index}`,
      sourceText,
      transferOperatorName,
      ruleText,
      timeText: formatTableBillingSectionTime(item.transferTime || item.checkIn),
      durationText: formatBillingDuration(segmentIsPreOpen ? 0 : getRecordStayMinutes(item), segmentIsPreOpen ? undefined : item.stayDuration),
      amount,
      originalAmount,
      rows,
      isTransfer: isTransferSection,
      isCurrent: isCurrentSegment,
      defaultExpanded: isCurrentSegment,
      canUpdateBillingType: canUpdateTransferBillingType(rollbackBatch),
      ...(rollbackBatch ? { rollbackBatch } : {}),
    }
  })

  return sections
    .filter(section => !shouldHideZeroAmountTransferSection(section, hideZeroAmountTransferSections))
    .sort((left, right) => {
      if (left.isCurrentSegment !== right.isCurrentSegment) {
        return left.isCurrentSegment ? -1 : 1
      }
      const timeDiff = right.timeValue - left.timeValue
      if (timeDiff !== 0) {
        return timeDiff
      }
      return right.originalIndex - left.originalIndex
    })
    .map(({ originalIndex: _originalIndex, isCurrentSegment: _isCurrentSegment, timeValue: _timeValue, ...section }) => section)
}

export const buildTableBillingDetailSectionsFromSegments = (
  segments: TableBillingSegmentSource[] | null | undefined,
  tableTransferBatchList: TableTransferBatchInfo[] = [],
  hideZeroAmountTransferSections = true,
  isPreOpen = false,
): TableBillingDetailSection[] => {
  if (!Array.isArray(segments) || segments.length === 0) {
    return []
  }

  const currentSegmentIndex = segments.findIndex(segment => segment.latest)
  const resolvedCurrentSegmentIndex = currentSegmentIndex >= 0 ? currentSegmentIndex : segments.length - 1
  const transferBatchAssignments = buildServerSegmentBatchAssignments(
    segments,
    tableTransferBatchList,
    resolvedCurrentSegmentIndex,
  )
  const sections = segments.map((segment, index) => {
    const isTransfer = segment.segmentSource === "TRANSFER"
    const shouldZeroPreOpenCharge = isPreOpen && !isTransfer
    const amountCalculated = shouldZeroPreOpenCharge || isServerSegmentAmountCalculated(segment)
    const hasExplicitTableFee = segment.tableFee !== undefined && segment.tableFee !== null
    const tableFee = shouldZeroPreOpenCharge ? 0 : toNonNegativeAmount(segment.tableFee)
    const serviceFee = shouldZeroPreOpenCharge ? 0 : toNonNegativeAmount(segment.serviceFee)
    const totalFee = shouldZeroPreOpenCharge
      ? 0
      : segment.totalFee !== undefined && segment.totalFee !== null
        ? toNonNegativeAmount(segment.totalFee)
        : tableFee + serviceFee
    const durationText = shouldZeroPreOpenCharge
      ? formatBillingDuration(0)
      : segment.stayDuration || formatBillingDuration(segment.stayMinutes)
    const rows: TableBillingDetailRow[] = []
    const packageFee = shouldZeroPreOpenCharge
      ? 0
      : segment.packageFee === undefined || segment.packageFee === null
        ? undefined
        : toNonNegativeAmount(segment.packageFee)
    const overtimeFee = shouldZeroPreOpenCharge
      ? 0
      : segment.overtimeFee === undefined || segment.overtimeFee === null
        ? undefined
        : toNonNegativeAmount(segment.overtimeFee)
    const packageChargeMinutes = shouldZeroPreOpenCharge ? 0 : segment.packageChargeMinutes
    const overtimeMinutes = shouldZeroPreOpenCharge ? 0 : segment.overtimeMinutes
    const ruleType = segment.ruleType ?? segment.billingRuleSummary?.ruleType
    const hasCompletePackageBreakdown = !shouldZeroPreOpenCharge
      && ruleType === 2
      && packageFee !== undefined
      && overtimeFee !== undefined
      && packageChargeMinutes !== undefined
      && packageChargeMinutes !== null
      && overtimeMinutes !== undefined
      && overtimeMinutes !== null
      && isNumberEqual(packageFee + overtimeFee, tableFee)

    if (hasCompletePackageBreakdown) {
      rows.push({
        key: `${segment.segmentId ?? index}-package`,
        label: "包段收费",
        durationText: formatBillingDuration(packageChargeMinutes),
        amount: packageFee,
        originalAmount: packageFee,
      })
      if (overtimeFee > 0 || overtimeMinutes > 0) {
        rows.push({
          key: `${segment.segmentId ?? index}-overtime`,
          label: "超时收费",
          durationText: formatBillingDuration(overtimeMinutes),
          amount: overtimeFee,
          originalAmount: overtimeFee,
        })
      }
    } else if (tableFee > 0 || serviceFee <= 0) {
      rows.push({
        key: `${segment.segmentId ?? index}-table`,
        label: "桌台费",
        durationText,
        amount: hasExplicitTableFee || shouldZeroPreOpenCharge ? tableFee : totalFee,
        originalAmount: hasExplicitTableFee || shouldZeroPreOpenCharge ? tableFee : totalFee,
      })
    }
    if (serviceFee > 0) {
      rows.push({
        key: `${segment.segmentId ?? index}-service`,
        label: "服务费",
        durationText,
        amount: serviceFee,
        originalAmount: serviceFee,
      })
    }
    rows.forEach((row) => {
      row.amountCalculated = amountCalculated
    })

    const isCurrentSegment = index === resolvedCurrentSegmentIndex
    const rollbackBatch = transferBatchAssignments.get(index)

    return {
      key: segment.segmentId || `billing-segment-${segment.segmentNo ?? index}`,
      sourceText: getServerSegmentSourceText(segment, rollbackBatch),
      transferOperatorName: rollbackBatch?.transferOperatorName || "",
      ruleText: segment.ruleName || segment.billingRuleSummaryText || segment.segmentSummaryText || segment.ruleTypeName || "",
      timeText: getServerSegmentTimeText(segment, rollbackBatch),
      durationText,
      amount: totalFee,
      originalAmount: totalFee,
      amountCalculated,
      rows,
      isTransfer,
      isCurrent: isCurrentSegment,
      defaultExpanded: isCurrentSegment || segments.length === 1,
      canUpdateBillingType: canUpdateTransferBillingType(rollbackBatch),
      ...(rollbackBatch ? { rollbackBatch } : {}),
    }
  })

  return sections
    .map((section, originalIndex) => ({
      originalIndex,
      section,
      segment: segments[originalIndex],
    }))
    .filter(item => !shouldHideZeroAmountTransferSegment(
      item.segment,
      item.section.rollbackBatch,
      hideZeroAmountTransferSections,
    ))
    .sort((left, right) => {
      if (left.section.isCurrent !== right.section.isCurrent) {
        return left.section.isCurrent ? -1 : 1
      }
      const leftStartTime = dayjs(left.segment.startTime).valueOf()
      const rightStartTime = dayjs(right.segment.startTime).valueOf()
      if (Number.isFinite(leftStartTime) && Number.isFinite(rightStartTime) && leftStartTime !== rightStartTime) {
        return rightStartTime - leftStartTime
      }
      const segmentNoDiff = (right.segment.segmentNo ?? -1) - (left.segment.segmentNo ?? -1)
      if (segmentNoDiff !== 0) {
        return segmentNoDiff
      }
      return right.originalIndex - left.originalIndex
    })
    .map(item => item.section)
}

export const hasTableBillingRowDiscount = (row: TableBillingDetailRow) => {
  return !isNumberEqual(row.amount || 0, row.originalAmount || 0)
}
